<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    useId,
} from 'vue';

type Option = { value: string; label: string };

const props = withDefaults(
    defineProps<{
        modelValue: string;
        options: Option[];
        placeholder: string;
        accessibleLabel: string;
        invalid?: boolean;
    }>(),
    { invalid: false },
);

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const dropdownId = useId();
const root = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);
const open = ref(false);
const activeIndex = ref(0);
const selectedLabel = computed(
    () =>
        props.options.find((option) => option.value === props.modelValue)
            ?.label ?? props.placeholder,
);

function openMenu(): void {
    activeIndex.value = Math.max(
        0,
        props.options.findIndex((option) => option.value === props.modelValue),
    );
    open.value = true;
    void nextTick(() => scrollActiveIntoView());
}

function closeMenu(): void {
    open.value = false;
}

function toggleMenu(): void {
    if (open.value) {
        closeMenu();
    } else {
        openMenu();
    }
}

function selectOption(option: Option): void {
    emit('update:modelValue', option.value);
    closeMenu();
}

function moveActive(direction: number): void {
    if (!open.value) {
        openMenu();

        return;
    }

    activeIndex.value =
        (activeIndex.value + direction + props.options.length) %
        props.options.length;
    void nextTick(() => scrollActiveIntoView());
}

function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
    } else if ((event.key === 'Enter' || event.key === ' ') && open.value) {
        event.preventDefault();
        const option = props.options[activeIndex.value];

        if (option) {
            selectOption(option);
        }
    } else if (event.key === 'Escape') {
        closeMenu();
    }
}

function scrollActiveIntoView(): void {
    const menuElement = menu.value;
    const activeOption = menuElement?.querySelector<HTMLElement>(
        `#${CSS.escape(optionId(activeIndex.value))}`,
    );

    if (!menuElement || !activeOption) {
        return;
    }

    menuElement.scrollTop = Math.max(
        0,
        activeOption.offsetTop -
            (menuElement.clientHeight - activeOption.offsetHeight) / 2,
    );
}

function optionId(index: number): string {
    return `${dropdownId}-option-${index}`;
}

function handlePointerDown(event: PointerEvent): void {
    if (!root.value?.contains(event.target as Node)) {
        closeMenu();
    }
}

onMounted(() => document.addEventListener('pointerdown', handlePointerDown));
onBeforeUnmount(() =>
    document.removeEventListener('pointerdown', handlePointerDown),
);
</script>

<template>
    <div
        ref="root"
        class="mini-dropdown"
        :class="{
            'mini-dropdown--open': open,
            'mini-dropdown--placeholder': !modelValue,
        }"
    >
        <button
            class="mini-dropdown__trigger"
            type="button"
            :aria-label="accessibleLabel"
            aria-haspopup="listbox"
            :aria-expanded="open"
            :aria-invalid="invalid || undefined"
            :aria-activedescendant="open ? optionId(activeIndex) : undefined"
            @click="toggleMenu"
            @keydown="handleKeydown"
        >
            <span>{{ selectedLabel }}</span>
            <ChevronDown :size="17" />
        </button>

        <Transition name="mini-dropdown">
            <div
                v-if="open"
                ref="menu"
                class="mini-dropdown__menu"
                role="listbox"
                :aria-label="accessibleLabel"
            >
                <button
                    v-for="(option, index) in options"
                    :id="optionId(index)"
                    :key="option.value"
                    class="mini-dropdown__option"
                    :class="{
                        'mini-dropdown__option--selected':
                            option.value === modelValue,
                        'mini-dropdown__option--active': index === activeIndex,
                    }"
                    type="button"
                    role="option"
                    :aria-selected="option.value === modelValue"
                    @pointerenter="activeIndex = index"
                    @click="selectOption(option)"
                >
                    <span>{{ option.label }}</span>
                </button>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.mini-dropdown {
    position: relative;
    min-width: 0;
}
.mini-dropdown--open {
    z-index: 30;
}
.mini-dropdown__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    height: 56px;
    padding: 0 14px;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text);
    /* Not /1. A line box exactly the height of the font has nothing below the baseline, and
       the `overflow: hidden` below — there to make the ellipsis work — then cuts the
       descenders off: «Червень» lost the tail of its р. The row is centred by flex and has
       a fixed 56px height, so a taller line box changes nothing else. */
    font: 500 15px/1.4 var(--app-font);
    text-align: left;
    cursor: pointer;
}
.mini-dropdown__trigger span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.mini-dropdown__trigger svg {
    flex: 0 0 auto;
    color: var(--hint);
    transition: transform 0.18s ease;
}
.mini-dropdown--open .mini-dropdown__trigger svg {
    transform: rotate(180deg);
}
.mini-dropdown--placeholder .mini-dropdown__trigger {
    color: var(--hint);
}
.mini-dropdown__menu {
    position: absolute;
    z-index: 1100;
    top: 100%;
    left: -1px;
    width: calc(100% + 2px);
    max-height: min(232px, 42dvh);
    padding: 0;
    scroll-padding-block: 12px;
    overflow-y: auto;
    overscroll-behavior: contain;
    border: 1px solid color-mix(in srgb, var(--hint) 28%, transparent);
    border-radius: 0 0 15px 15px;
    background: color-mix(in srgb, var(--bg) 92%, var(--surface));
    box-shadow:
        0 18px 34px rgba(3, 13, 25, 0.14),
        inset 0 1px 0 color-mix(in srgb, white 16%, transparent);
    backdrop-filter: blur(18px);
    scrollbar-width: none;
}
.mini-dropdown__menu::-webkit-scrollbar {
    display: none;
}
.mini-dropdown__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 46px;
    padding: 0 14px;
    border: 0;
    border-bottom: 1px solid color-mix(in srgb, var(--hint) 22%, transparent);
    border-radius: 0;
    background: transparent;
    color: var(--text);
    font: 500 15px/1.2 var(--app-font);
    text-align: left;
    cursor: pointer;
}
.mini-dropdown__option:last-child {
    border-bottom: 0;
}
.mini-dropdown__option--active {
    background: color-mix(in srgb, var(--hint) 7%, transparent);
}
.mini-dropdown__option--selected {
    color: var(--accent);
    font-weight: 600;
}
.mini-dropdown-enter-active,
.mini-dropdown-leave-active {
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;
}
.mini-dropdown-enter-from,
.mini-dropdown-leave-to {
    transform: translateY(-5px) scale(0.98);
    opacity: 0;
}
</style>
